import { getCookie } from './Cookies';
import type { AgentContext } from '../hooks/useAgentContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export interface ChatResponse {
    conversationId: string;
}

export type SSEEventType = 'text' | 'tool_call' | 'tool_result' | 'tool_error' | 'chart' | 'confirmation_required' | 'data_changed' | 'done' | 'error' | 'unavailable' | 'conversation_id';

export interface SSEEvent {
    event: SSEEventType;
    data: string;
}

export interface ToolCallInfo {
    tool_call_id: string;
    tool_name: string;
    description: string;
}

export interface ConfirmationPayload {
    tool_call_id: string;
    tool_name: string;
    preview: string;
    tool_input: Record<string, unknown>;
}

export interface DisplayMessage {
    role: 'user' | 'assistant';
    content: string;
    tool_calls?: ToolCallInfo[];
    charts?: Record<string, unknown>[];
}

export interface ConversationSummary {
    conversation_id: string;
    title: string | null;
    updated_at: string;
}

export interface ConversationDetail {
    conversation_id: string;
    title: string | null;
    context: Record<string, unknown> | null;
    messages: DisplayMessage[];
    updated_at: string;
}

function getAuthHeaders(): Record<string, string> {
    const token = getCookie('accessToken');
    const tokenType = getCookie('tokenType');
    return {
        'Content-Type': 'application/json',
        'Authorization': `${tokenType} ${token}`,
    };
}

/** Race a promise against a timeout. Rejects with a user-friendly message. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Connection lost — please try again')), ms);
        promise.then(
            val => { clearTimeout(timer); resolve(val); },
            err => { clearTimeout(timer); reject(err); },
        );
    });
}

// Must be longer than backend keepalive (5s) — if no data for 3min, connection is dead.
const READ_TIMEOUT_MS = 180_000;

/**
 * Parse an SSE stream from a ReadableStream.
 * Handles \r\n and \n line endings, and events split across chunks.
 */
async function* parseSseStream(
    body: ReadableStream<Uint8Array>,
): AsyncGenerator<{ event: string; data: string }> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent: string | null = null;
    let currentData: string | null = null;

    try {
        while (true) {
            const { done, value } = await withTimeout(reader.read(), READ_TIMEOUT_MS);
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            // Normalise line endings: \r\n → \n
            buffer = buffer.replace(/\r\n/g, '\n');

            const lines = buffer.split('\n');
            // Keep last (possibly incomplete) line in the buffer
            buffer = lines.pop() ?? '';

            for (const line of lines) {
                if (line.startsWith('event:')) {
                    currentEvent = line.slice(6).trim();
                } else if (line.startsWith('data:')) {
                    // Per SSE spec: strip exactly one leading space after "data:", if present
                    const raw = line.slice(5);
                    const value = raw.startsWith(' ') ? raw.slice(1) : raw;
                    // SSE spec: multiple "data:" lines are joined with newlines
                    currentData = currentData === null ? value : currentData + '\n' + value;
                } else if (line.trim() === '' && currentEvent !== null) {
                    yield { event: currentEvent, data: currentData ?? '' };
                    currentEvent = null;
                    currentData = null;
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}

/**
 * Send a chat message and stream SSE events back.
 */
export async function* streamChat(
    message: string,
    conversationId: string | null,
    context: AgentContext,
    signal?: AbortSignal,
    autoConfirm?: boolean,
): AsyncGenerator<SSEEvent> {
    const response = await fetch(`${API_URL}/agent/chat`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ message, conversation_id: conversationId, context, auto_confirm: autoConfirm ?? false }),
        signal,
    });

    if (!response.ok) {
        throw new Error(`Agent API error: ${response.status}`);
    }
    if (!response.body) {
        throw new Error('No response body');
    }

    for await (const { event, data } of parseSseStream(response.body)) {
        yield { event: event as SSEEventType, data };
    }
}

/**
 * Send a confirmation/cancellation for a write tool.
 */
export async function* streamConfirmation(
    conversationId: string,
    toolCallId: string,
    confirmed: boolean,
    signal?: AbortSignal,
    autoConfirm?: boolean,
): AsyncGenerator<SSEEvent> {
    const response = await fetch(`${API_URL}/agent/confirm`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ conversation_id: conversationId, tool_call_id: toolCallId, confirmed, auto_confirm: autoConfirm ?? false }),
        signal,
    });

    if (!response.ok) {
        throw new Error(`Agent confirm API error: ${response.status}`);
    }
    if (!response.body) {
        throw new Error('No response body');
    }

    for await (const { event, data } of parseSseStream(response.body)) {
        yield { event: event as SSEEventType, data };
    }
}

/**
 * Resume a stalled conversation. No user message is added to history.
 */
export async function* streamNudge(
    conversationId: string,
    signal?: AbortSignal,
): AsyncGenerator<SSEEvent> {
    const response = await fetch(`${API_URL}/agent/nudge`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ conversation_id: conversationId }),
        signal,
    });

    if (!response.ok) {
        throw new Error(`Agent nudge API error: ${response.status}`);
    }
    if (!response.body) {
        throw new Error('No response body');
    }

    for await (const { event, data } of parseSseStream(response.body)) {
        yield { event: event as SSEEventType, data };
    }
}

/**
 * Fetch the user's conversation history.
 */
export async function getConversations(): Promise<ConversationSummary[]> {
    const response = await fetch(`${API_URL}/agent/conversations`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.status}`);
    }
    return response.json();
}

/**
 * Fetch a single conversation with display-formatted messages.
 */
export async function getConversation(conversationId: string): Promise<ConversationDetail> {
    const response = await fetch(`${API_URL}/agent/conversations/${conversationId}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch conversation: ${response.status}`);
    }
    return response.json();
}

/**
 * Delete a conversation.
 */
export async function deleteConversation(conversationId: string): Promise<void> {
    const response = await fetch(`${API_URL}/agent/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        throw new Error(`Failed to delete conversation: ${response.status}`);
    }
}

/**
 * Rename a conversation.
 */
export async function renameConversation(conversationId: string, title: string): Promise<void> {
    const response = await fetch(`${API_URL}/agent/conversations/${conversationId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title }),
    });
    if (!response.ok) {
        throw new Error(`Failed to rename conversation: ${response.status}`);
    }
}
